import { useCallback, type KeyboardEvent } from "react";

const NAVIGATION_KEYS = new Set(["ArrowLeft", "ArrowRight", "Home", "End"]);

export function getNextTab<T>(tabs: readonly T[], activeTab: T, key: string): T | null {
  if (tabs.length === 0 || !NAVIGATION_KEYS.has(key)) return null;
  if (key === "Home") return tabs[0];
  if (key === "End") return tabs[tabs.length - 1];
  const current = Math.max(tabs.indexOf(activeTab), 0);
  const step = key === "ArrowRight" ? 1 : -1;
  return tabs[(current + step + tabs.length) % tabs.length];
}

/**
 * Arrow/Home/End keys and a single tab stop for an existing `role="tablist"`,
 * so tabs keep their own markup and styling. `tabs` must be in DOM order.
 */
export default function useTabListKeyboard<T extends string>(
  tabs: readonly T[],
  activeTab: T,
  onSelect: (tab: T) => void
) {
  const focusableTab = tabs.includes(activeTab) ? activeTab : tabs[0];

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const next = getNextTab(tabs, activeTab, event.key);
      if (next === null) return;
      event.preventDefault();
      const tabElements = event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]');
      tabElements[tabs.indexOf(next)]?.focus();
      if (next !== activeTab) onSelect(next);
    },
    [tabs, activeTab, onSelect]
  );

  const getTabIndex = useCallback((tab: T) => (tab === focusableTab ? 0 : -1), [focusableTab]);

  return { onKeyDown, getTabIndex };
}
